import React from "react";

import { filter, find, intersection, isEmpty, map } from "lodash/fp";

import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { UserDistributionSetting } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

import Checkbox from "@rui/Checkbox";
import { Row } from "@rui/Stack";

import { TOPIC_REPORT_DISTRIBUTION_CHANNELS } from "../constants";
import DistributionChannelRow from "../DistributionChannelRow";
import ReportingSettingAccordion from "../ReportingSettingAccordion";
import { AccordionDetailsContent } from "../styles";
import * as ReportingSettingUtils from "../utils";

const DEFAULT_TOPIC_REPORT_DISTRIBUTION: Omit<
  UserDistributionSetting,
  "method"
> = {
  category: "topic_summary",
  isEnabled: true,
  frequency: "daily",
  sendTo: "me",
};

const TopicSummarySettings = () => {
  const { userSettings, updateUserSettings } = useUserSettings();
  const featureAccess = useFeatureAccess();
  const { isProviderConnected } = useIntegrations();

  const topicReportSettings = ReportingSettingUtils.getSettingsForCategory(
    userSettings,
    "topic_summary"
  );

  const enabledChannelSettings =
    ReportingSettingUtils.getEnabledChannelSettings(topicReportSettings);

  const settingDisabledOpts = UserSettingsUtils.getSettingDisabledOpts({
    settingIsEditable: featureAccess.canEditSendTopicReport,
  });

  // channels that the user is able to connect (excludes channels that have been disabled by the Workspace)
  const connectableChannels: UserDistributionSetting["method"][] = map(
    "channel",
    filter(
      (c) => !c.providerId || featureAccess.canAccessIntegration(c.providerId),
      TOPIC_REPORT_DISTRIBUTION_CHANNELS
    )
  );

  // channels that are currently connected
  const connectedChannels: UserDistributionSetting["method"][] = map(
    "channel",
    filter(
      (c) => !c.providerId || isProviderConnected({ providerId: c.providerId }),
      TOPIC_REPORT_DISTRIBUTION_CHANNELS
    )
  );

  const availableChannelsForUser = intersection(
    connectableChannels,
    connectedChannels
  );

  const handleToggleEnabled = (enable: boolean) => {
    const updatedSettings =
      enable && isEmpty(topicReportSettings)
        ? availableChannelsForUser.map((method) => ({
            ...DEFAULT_TOPIC_REPORT_DISTRIBUTION,
            method,
          }))
        : topicReportSettings.map((setting) => ({
            ...setting,
            isEnabled: enable,
          }));

    updateUserSettings.mutate({ userDistributionSettings: updatedSettings });
  };

  const handleToggleChannelEnabled = (
    channel: UserDistributionSetting["method"],
    enable: boolean,
    existingSetting?: UserDistributionSetting
  ) => {
    const updatedSetting = existingSetting
      ? { ...existingSetting, isEnabled: enable }
      : {
          ...DEFAULT_TOPIC_REPORT_DISTRIBUTION,
          method: channel,
          isEnabled: enable,
        };

    updateUserSettings.mutate({ userDistributionSettings: [updatedSetting] });
  };

  const handleToggleIncludeUpdates =
    (existingSetting: UserDistributionSetting) =>
    (_e: React.SyntheticEvent, checked: boolean) => {
      const updatedSetting = {
        ...existingSetting,
        includeUpdatesToExistingTopics: checked,
      };
      updateUserSettings.mutate({ userDistributionSettings: [updatedSetting] });
    };

  return (
    <ReportingSettingAccordion
      title="New Readouts"
      subtitle="When Read generates a new Readout from email or messaging, automatically notify me via these channels"
      enabledChannels={enabledChannelSettings}
      onToggleEnabled={handleToggleEnabled}
      opts={{
        disabled: settingDisabledOpts.disabled,
        disabledTooltip: settingDisabledOpts.tooltip ?? "",
      }}
    >
      <AccordionDetailsContent>
        {TOPIC_REPORT_DISTRIBUTION_CHANNELS.map((channel) => {
          const userSetting = find(
            { method: channel.channel },
            topicReportSettings
          );

          const channelIsDisabled =
            settingDisabledOpts.disabled ||
            !availableChannelsForUser.includes(channel.channel);

          const channelIsEnabled =
            !channelIsDisabled && Boolean(userSetting?.isEnabled);

          const disabledReason = ReportingSettingUtils.getChannelDisabledReason(
            {
              isConnected: connectedChannels.includes(channel.channel),
              isDisabled: !connectableChannels.includes(channel.channel),
            }
          );

          return (
            <DistributionChannelRow
              key={channel.channel}
              config={channel}
              name="topic-reports-distribution"
              defaultFrequency={DEFAULT_TOPIC_REPORT_DISTRIBUTION.frequency}
              defaultSendTo={DEFAULT_TOPIC_REPORT_DISTRIBUTION.sendTo}
              disabledReason={disabledReason}
              onToggleEnabled={handleToggleChannelEnabled}
              setting={userSetting}
            >
              {channelIsEnabled && (
                <Row marginLeft={4}>
                  <FormControlLabel
                    checked={Boolean(
                      userSetting?.includeUpdatesToExistingTopics
                    )}
                    control={<Checkbox variant="filled" />}
                    label={
                      <Typography
                        variant="bodySecondaryReg"
                        color="shades.neutral.600"
                      >
                        Include Readout report updates
                      </Typography>
                    }
                    onChange={handleToggleIncludeUpdates(userSetting!)}
                  />
                </Row>
              )}
            </DistributionChannelRow>
          );
        })}
      </AccordionDetailsContent>
    </ReportingSettingAccordion>
  );
};

export default TopicSummarySettings;
